import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminNews() {
  const [articles, setArticles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', source: '', summary: '', url: '', tags: '', personas: '', featured: false });
  const [saving, setSaving] = useState(false);
  const [ingesting, setIngesting] = useState(false);

  const load = () => api.get('/admin/news').then(r => setArticles(r.data.articles || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleFeatured = async (article) => {
    await api.put(`/news/${article.id}`, { featured: !article.featured });
    toast.success(article.featured ? 'Unfeatured' : 'Featured');
    load();
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Remove this article?')) return;
    await api.delete(`/news/${id}`);
    toast.success('Article removed');
    load();
  };

  const handleIngest = async () => {
    setIngesting(true);
    try {
      const r = await api.post('/admin/news/ingest');
      if (r.data.added > 0) {
        toast.success(`${r.data.added} new article${r.data.added === 1 ? '' : 's'} fetched`);
        load();
      } else {
        toast.success('No new articles found — already up to date');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ingestion failed');
    } finally {
      setIngesting(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/news', {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        personas: form.personas.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Article added');
      setForm({ title: '', source: '', summary: '', url: '', tags: '', personas: '', featured: false });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="section-tag">Admin</div>
      <h1 className="section-title">NEWS <span style={{ color: 'var(--neon-cyan)' }}>MANAGEMENT</span></h1>
      <p style={{ color: 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', margin: '0.5rem 0 1.5rem' }}>{articles.length} articles indexed</p>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Cancel' : '+ Add Article'}
        </button>
        <button className="btn btn-secondary" onClick={handleIngest} disabled={ingesting} style={{ borderColor: 'rgba(0,229,255,0.3)', color: 'var(--neon-cyan)' }}>
          {ingesting ? 'Fetching...' : '↻ Fetch Latest'}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, marginBottom: '1rem' }}>Add New Article</div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="detail-grid">
              <div className="form-group">
                <label className="form-label">Source *</label>
                <input className="form-input" type="text" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="PIB, CEEW, etc." required />
              </div>
              <div className="form-group">
                <label className="form-label">URL *</label>
                <input className="form-input" type="url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Summary *</label>
              <textarea className="form-textarea" rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} required />
            </div>
            <div className="detail-grid">
              <div className="form-group">
                <label className="form-label">Tags (comma-separated)</label>
                <input className="form-input" type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="CCTS, Policy, Compliance" />
              </div>
              <div className="form-group">
                <label className="form-label">Personas (comma-separated)</label>
                <input className="form-input" type="text" value={form.personas} onChange={e => setForm({ ...form, personas: e.target.value })} placeholder="Corporate, Investor / Analyst" />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted)' }}>
              <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} />
              Feature this article (pinned in news feed)
            </label>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Article'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </form>
      )}

      <div className="card">
        <div style={{ overflow: 'auto' }} className="thin-scroll">
          <table className="data-table" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Source</th>
                <th>Tags</th>
                <th>Personas</th>
                <th>Featured</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a.id}>
                  <td>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--neon-cyan)', fontSize: '0.82rem', textDecoration: 'none' }}>{a.title}</a>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>{a.source}</td>
                  <td style={{ fontSize: '0.65rem' }}>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {(a.tags || []).map(t => <span key={t} className="badge badge-muted" style={{ fontSize: '0.55rem' }}>{t}</span>)}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.65rem', color: 'var(--muted)' }}>{(a.personas || []).join(', ') || 'All'}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${a.featured ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleFeatured(a)}
                    >
                      {a.featured ? 'Featured' : 'Feature'}
                    </button>
                  </td>
                  <td style={{ color: 'var(--muted2)', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                    {new Date(a.published_at).toLocaleDateString('en-IN')}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteArticle(a.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
